import { useRef, useState, useCallback, useEffect } from 'react'

// Shared drag mechanism for floating widgets. Uses pointer events (not
// separate mouse/touch handlers) so mobile and desktop work through one
// code path -- the exact kind of split-handling mistake that caused the
// sidebar swipe gesture to live in the wrong place earlier tonight.
// Dragging is by the header/handle only, not the whole panel, so it
// doesn't interfere with clicking calculator buttons or calendar days.
export function useDraggable() {
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragging = useRef(false)
  const start = useRef({ x: 0, y: 0, offX: 0, offY: 0 })

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = true
    start.current = { x: e.clientX, y: e.clientY, offX: offset.x, offY: offset.y }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [offset])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    setOffset({
      x: start.current.offX + (e.clientX - start.current.x),
      y: start.current.offY + (e.clientY - start.current.y),
    })
  }, [])

  const onPointerUp = useCallback(() => {
    dragging.current = false
  }, [])

  // Reset position when the widget closes and reopens, rather than
  // reappearing wherever it was last dragged to, which would be
  // confusing rather than convenient.
  const resetOffset = useCallback(() => setOffset({ x: 0, y: 0 }), [])

  return { offset, handleProps: { onPointerDown, onPointerMove, onPointerUp }, resetOffset }
}

// Fires a real browser Notification (not routed through any backend) when
// a stored reminder's time arrives, plus keeps a small localStorage-backed
// list so reminders survive a reload on this same device. Checked on an
// interval while the dashboard tab is open -- honest limitation, not
// claimed as equivalent to a true push notification that survives the
// tab/browser being fully closed, which would need backend storage plus a
// scheduled check to send a real push, deliberately not built here.
export interface Reminder {
  id: string
  date: string // YYYY-MM-DD
  time: string // HH:MM
  note: string
  fired: boolean
}

const REMINDERS_KEY = 'ops-bridge-reminders'

export function loadReminders(): Reminder[] {
  try {
    const raw = localStorage.getItem(REMINDERS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export function saveReminders(reminders: Reminder[]) {
  try {
    localStorage.setItem(REMINDERS_KEY, JSON.stringify(reminders))
  } catch { /* storage unavailable -- reminder just won't persist across reload */ }
}

export function useReminderChecker() {
  useEffect(() => {
    const interval = setInterval(() => {
      const reminders = loadReminders()
      const now = new Date()
      let changed = false
      const updated = reminders.map(r => {
        if (r.fired) return r
        const due = new Date(`${r.date}T${r.time}`)
        if (now >= due) {
          changed = true
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Reminder', { body: r.note, tag: r.id })
          }
          return { ...r, fired: true }
        }
        return r
      })
      if (changed) saveReminders(updated)
    }, 30_000)
    return () => clearInterval(interval)
  }, [])
}
